import { AGGREGATE_SCORES, AG_RECRUITMENT_MEMBER_STATUS } from '../constants';

/** Labels align with row color bands and the Panel Selection progress bar. */
export function getPanelRankingBandLabel(ranking) {
    const r = Number(ranking);
    if (Number.isNaN(r)) return '';
    if (r >= 90 && r <= 100) return AGGREGATE_SCORES.C;
    if (r >= 75 && r < 90) return AGGREGATE_SCORES.M;
    if (r >= 50 && r < 75) return AGGREGATE_SCORES.P;
    if (r >= 0 && r < 50) return AGGREGATE_SCORES.N;
    return '';
}

/**
 * Canonical API explanation is `panelFitExplanation`; use `getPanelRankingSupplementalDetail` only for
 * legacy string fields when that is absent. Structured drivers live on `rankingBreakdown`.
 */
export function getRankingBreakdownTopContributions(member) {
    const b = member?.rankingBreakdown;
    if (!b || typeof b !== 'object') return [];
    const raw = b.topContributions ?? b.representativenessContribution;
    return Array.isArray(raw) ? raw : [];
}

/** Plain-text block for tooltip: human-readable driver lines (uses `label`). */
export function formatRankingContributionsForTooltip(items, maxItems = 5) {
    if (!Array.isArray(items) || !items.length) return '';
    const lines = items.slice(0, maxItems).map((c) => {
        const label = String(c.label || c.dimension || '').trim();
        if (!label) return null;
        if (typeof c.contribution === 'number' && !Number.isNaN(c.contribution)) {
            const sign = c.contribution > 0 ? '+' : '';
            return `• ${label} (${sign}${c.contribution.toFixed(2)})`;
        }
        return `• ${label}`;
    }).filter(Boolean);
    if (!lines.length) return '';
    return ['Top drivers', ...lines].join('\n');
}

/**
 * Reads cohort age quartile upper bounds (years) from `panelRankingContext` when the API sends them.
 * Same semantics as analytics baseline ages: Q1 ≤ q1, Q2 is (q1+1)–q2, Q3 is (q2+1)–q3, Q4 ≥ q3+1.
 * Accepts: flat `baselineAgeQ1`…`Q3`, or nested `ageQuartileCutpointsYears` / `cohortAgeQuartileCutpoints`.
 */
export function getCohortAgeQuartileCutpoints(panelRankingContext) {
    const ctx = panelRankingContext;
    if (!ctx || typeof ctx !== 'object') return null;

    const readTriple = (obj) => {
        if (!obj || typeof obj !== 'object') return null;
        const q1 = obj.baselineAgeQ1 ?? obj.q1Max ?? obj.maxAgeQ1;
        const q2 = obj.baselineAgeQ2 ?? obj.q2Max ?? obj.maxAgeQ2;
        const q3 = obj.baselineAgeQ3 ?? obj.q3Max ?? obj.maxAgeQ3;
        if (q1 == null || q2 == null || q3 == null) return null;
        const n1 = Number(q1);
        const n2 = Number(q2);
        const n3 = Number(q3);
        if ([n1, n2, n3].some((n) => Number.isNaN(n))) return null;
        return { q1: n1, q2: n2, q3: n3 };
    };

    const nested =
        ctx.ageQuartileCutpointsYears ||
        ctx.cohortAgeQuartileCutpoints ||
        ctx.ageQuartileBoundaries;
    return readTriple(nested) || readTriple(ctx);
}

/** Injects “Q2 (35–48 yrs)” style segments into any string that contains age quartile codes. */
export function enrichTextWithAgeQuartileRanges(text, cutpoints) {
    if (!text || typeof text !== 'string' || !cutpoints) return text;
    if (!/\bQ[1-4]\b/i.test(text)) return text;
    const { q1, q2, q3 } = cutpoints;
    const band = {
        1: `≤${q1} yrs`,
        2: `${q1 + 1}–${q2} yrs`,
        3: `${q2 + 1}–${q3} yrs`,
        4: `≥${q3 + 1} yrs`,
    };
    return text.replace(/\bQ([1-4])\b/gi, (full, digit) => {
        const q = Number(digit);
        return `Q${q} (${band[q]})`;
    });
}

/**
 * Display label for one gap row: prefers API `ageRangeLabel`, then enriches `label` with cohort cutpoints.
 */
export function formatPanelGapDisplayLabel(gap, panelRankingContext) {
    if (!gap || typeof gap !== 'object') return '';
    if (typeof gap.ageRangeLabel === 'string' && gap.ageRangeLabel.trim()) {
        return gap.ageRangeLabel.trim();
    }
    const raw = typeof gap.label === 'string' ? gap.label : '';
    if (!raw) return '';
    if (!/\bage\b/i.test(raw) || !/\bQ[1-4]\b/i.test(raw)) return raw;
    const cutpoints = getCohortAgeQuartileCutpoints(panelRankingContext);
    return enrichTextWithAgeQuartileRanges(raw, cutpoints) || raw;
}

export function panelRankingContextMentionsAgeQuartiles(panelRankingContext) {
    const ctx = panelRankingContext;
    if (!ctx || typeof ctx !== 'object') return false;
    const mentions = (s) =>
        typeof s === 'string' && /\bage\b/i.test(s) && /\bQ[1-4]\b/i.test(s);
    if (mentions(ctx.panelAggregateSummary)) return true;
    const gaps = ctx.aggregateUnderrepresentation;
    if (!Array.isArray(gaps)) return false;
    return gaps.some((g) => mentions(g?.label));
}

/** Legacy / optional string narrative when `panelFitExplanation` is not sent. */
export function getPanelRankingSupplementalDetail(member) {
    if (!member || typeof member !== 'object') return null;
    const keys = ['panelFitExplanation', 'rankingDetail', 'rankingReason', 'rankingNotes'];
    for (const k of keys) {
        const v = member[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
}

/** True when an application id exists (recruited / linked application path). */
export function hasMeaningfulApplicationId(applicationId) {
    if (applicationId === null || applicationId === undefined) return false;
    if (typeof applicationId === 'string' && applicationId.trim() === '') return false;
    if (applicationId === 0) return false;
    return true;
}

/**
 * Canonical id from PUT .../advisory-group/member/:agMemberId/process-recruitment-outreach success JSON.
 * Prefer `applicationId` (always returned on success). Use `createdApplicationId` only as fallback (first insert).
 * Works with flat bodies or nested `data` after interceptors.
 */
export function pickApplicationIdFromProcessRecruitmentOutreachResponse(body) {
    if (body == null || typeof body !== 'object') return undefined;
    const candidates = [];
    const push = (v) => {
        if (v !== undefined) candidates.push(v);
    };
    push(body.applicationId);
    push(body.createdApplicationId);
    push(body.createdApplicationID);
    const nested = body.data;
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
        push(nested.applicationId);
        push(nested.createdApplicationId);
        push(nested.createdApplicationID);
    }
    for (const c of candidates) {
        if (hasMeaningfulApplicationId(c)) return c;
    }
    return undefined;
}

/**
 * AG roster row is an augmented placeholder: augmented status/flag but no application yet.
 * After recruitment, applicationId is set — use normal application-sourced behavior.
 */
export function isAugmentedPlaceholderMember(member) {
    if (!member) return false;
    const isAugmentedStatus = member.memberStatus === AG_RECRUITMENT_MEMBER_STATUS.Augmented;
    const isAugFlag =
        member.augmentationFlag === 'Y' ||
        member.augmentationFlag === 'y' ||
        member.augmentationFlag === true;
    if (!isAugmentedStatus && !isAugFlag) return false;
    return !hasMeaningfulApplicationId(member.applicationId);
}

/** Cohort member id on the AG member row (CohortManagement.Member PK). */
export function getCohortMemberIdFromAgRow(member) {
    if (!member) return null;
    const id = member.memberId ?? member.cohortMemberId;
    return id === undefined || id === null ? null : id;
}

/**
 * Merge CohortManagement.Member-shaped payload into an AG recruiting/outreach row
 * so list + modals can use the same field names as application-backed rows.
 */
export function mergeCohortMemberIntoAgOutreachRow(agRow, cohortMember) {
    if (!agRow || !cohortMember) return agRow;

    const cm = cohortMember;

    return {
        ...agRow,
        firstName: cm.firstName ?? agRow.firstName,
        lastName: cm.lastName ?? agRow.lastName,
        dateOfBirth: cm.birthday ?? agRow.dateOfBirth,
        city: cm.city ?? agRow.city,
        primaryLanguage: cm.primaryLanguage ?? cm.language ?? agRow.primaryLanguage,
        phone: cm.phone ?? agRow.phone,
        email: cm.email ?? agRow.email,
        healthPlanMemberId: cm.healthPlanMemberId ?? agRow.healthPlanMemberId,
        addressLine1: cm.addressLine1 ?? agRow.addressLine1,
        addressLine2: cm.addressLine2 ?? agRow.addressLine2,
        state: cm.state ?? agRow.state,
        postalCode: cm.postalCode ?? agRow.postalCode,
        applicationFirstName: cm.firstName ?? agRow.applicationFirstName,
        applicationLastName: cm.lastName ?? agRow.applicationLastName,
        applicationBirthday: cm.birthday ?? agRow.applicationBirthday,
        applicationAddress: cm.addressLine1 ?? agRow.applicationAddress,
        applicationAddress2: cm.addressLine2 ?? agRow.applicationAddress2,
        applicationCity: cm.city ?? agRow.applicationCity,
        applicationState: cm.state ?? agRow.applicationState,
        applicationPostalCode: cm.postalCode ?? agRow.applicationPostalCode,
        applicationPhone: cm.phone ?? agRow.applicationPhone,
        applicationEmail: cm.email ?? agRow.applicationEmail,
        applicationHealthPlanId: cm.healthPlanMemberId ?? agRow.applicationHealthPlanId,
        applicationPrimaryLanguage: cm.primaryLanguage ?? cm.language ?? agRow.applicationPrimaryLanguage,
    };
}

export function getCohortMemberFromListById(cohortMembersList, memberId) {
    if (!cohortMembersList?.length || memberId === undefined || memberId === null) return undefined;
    return cohortMembersList.find(
        (m) => m.id === memberId || String(m.id) === String(memberId) || Number(m.id) === Number(memberId)
    );
}

/** True when panel-potential-members API resolved a venue ZIP (show Distance column). */
export function hasPanelProximityMeetingZip(panelRankingContext) {
    const zip = panelRankingContext?.proximityMeetingZip;
    return zip != null && String(zip).trim() !== '';
}

/**
 * Tooltip copy for the panel "Distance (mi)" column: what the miles are measured from/to.
 * Panel fit / list order are demographic-only; distance is informational.
 * @param {object|null|undefined} panelRankingContext from panel-potential-members API
 * @returns {string}
 */
export function getPanelDistanceColumnTooltipText(panelRankingContext) {
    const ctx = panelRankingContext;
    const zip = ctx?.proximityMeetingZip;

    const base =
        'Straight-line distance in miles from the member ZIP code to the meeting venue, using ZIP-code centroids (approximate).';

    if (zip) {
        const parts = [
            base,
            `Reference point: meeting venue ZIP ${zip}.`,
            'Panel fit score and member order are based on demographic representativeness only; distance does not change ranking.',
        ];
        return parts.join(' ');
    }

    return (
        `${base} `
        + 'If this column shows — for everyone, no meeting venue ZIP was resolved (for example, no upcoming meeting with a location ZIP) or members lack a usable ZIP in the dataset.'
    );
}
